// Shared helpers & small components for the Spaces → Goals/Projects work layer.
import React from "react";
import { cn } from "../lib/utils";

// Priority quadrant (computed from impact/effort on the backend).
export const QUADRANT_META = {
  quick_win: { color: "#10B981" },
  major_bet: { color: "#2563EB" },
  fill_in: { color: "#F59E0B" },
  thankless: { color: "#EF4444" },
};

// Work status (goals & projects) + task statuses.
export const WORK_STATUSES = ["pending", "on_track", "at_risk", "paused", "done"];
export const TASK_STATUSES = ["open", "in_progress", "done"];

const STATUS_META = {
  pending: { bg: "rgba(245,158,11,0.18)", color: "#FBBF24" },
  on_track: { bg: "rgba(16,185,129,0.18)", color: "#34D399" },
  at_risk: { bg: "rgba(239,68,68,0.18)", color: "#F87171" },
  paused: { bg: "rgba(161,161,170,0.18)", color: "#A1A1AA" },
  done: { bg: "rgba(59,130,246,0.18)", color: "#60A5FA" },
  open: { bg: "rgba(245,158,11,0.18)", color: "#FBBF24" },
  in_progress: { bg: "rgba(16,185,129,0.18)", color: "#34D399" },
};

export function statusLabel(status, lang) {
  const M = {
    pending: ["En attente", "Pending"],
    on_track: ["En bonne voie", "On track"],
    at_risk: ["À risque", "At risk"],
    paused: ["En pause", "Paused"],
    done: ["Terminé", "Done"],
    open: ["Ouvert", "Open"],
    in_progress: ["En cours", "In progress"],
  };
  const m = M[status] || [status, status];
  return lang === "fr" ? m[0] : m[1];
}

export function WorkStatusPill({ status, lang }) {
  const s = STATUS_META[status] || { bg: "var(--muted)", color: "var(--text-secondary)" };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: s.bg, color: s.color }}
      data-testid={`status-pill-${status}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: s.color }} />
      {statusLabel(status, lang)}
    </span>
  );
}

export function ProgressBar({ value = 0, color = "#2563EB", className }) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="h-1.5 rounded-full overflow-hidden flex-1" style={{ background: "var(--muted)" }}>
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.max(0, Math.min(100, value))}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs text-text-secondary tabular-nums w-9 text-right">{value}%</span>
    </div>
  );
}

export function QuadrantBadge({ quadrant, t }) {
  if (!quadrant) return null;
  const cfg = QUADRANT_META[quadrant];
  if (!cfg) return null;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ backgroundColor: `${cfg.color}22`, color: cfg.color }} data-testid={`quadrant-badge-${quadrant}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cfg.color }} />
      {t(quadrant)}
    </span>
  );
}

// Priority presets → numeric impact/effort (0-10), matching the quadrant logic (>=6 = high).
export const PRIORITY_LEVELS = [
  { key: "low", value: 3 },
  { key: "medium", value: 5 },
  { key: "high", value: 8 },
];

export function levelFromValue(v) {
  if (v == null) return "";
  return v >= 6 ? "high" : v >= 4 ? "medium" : "low";
}
